import {kinks} from '@turf/turf';
import * as geojson from 'geojson';
import * as I from 'immutable';
import mapboxgl from 'mapbox-gl';
import React, {useMemo} from 'react';

import {
  EPA_SUPERFUND_SITES_FEATURE_COLLECTION_ID,
  INDIGENOUS_TERRITORIES_FEATURE_COLLECTION_ID,
  PLSS_FEATURE_COLLECTION_ID,
  PLSS_SECTIONS_FEATURE_COLLECTION_ID,
  PLSS_TOWNSHIPS_FEATURE_COLLECTION_ID,
} from 'app/components/Library/overlaysConfig';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  ApiFeatureCollectionOverlayMetadataView,
  GeometryOverlaySetting,
  HfcWithHoverKey,
  HoverProperty,
  HydratedFeatureCollection,
  PropertyColorRule,
} from 'app/modules/Remote/FeatureCollection';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import * as CONSTANTS from 'app/utils/constants';
import {REGRID_FEATURE_COLLECTION_ID} from 'app/utils/constants';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import {compileGeometry, getPolygonGeometryTypesFrom} from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as hookUtils from 'app/utils/hookUtils';
import {
  FeatureCollectionSourceRecord,
  getSourceForFeatureCollection,
  useAreClickableFeaturesDisabled,
  useClickableFeatures,
  useFilledPolygonLayer,
} from 'app/utils/mapUtils';
import loadTiledFeatureCollectionAsGeoJSON from 'app/utils/mapUtils/loadTileAsGeoJSON';
import {escapeHtml, safeUrl} from 'app/utils/stringUtils';

import cs from './GeometryOverlaysContent.module.scss';
import MapContent from './MapContent';

interface Props {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
  isOverlayVisibleByName: Record<string, boolean>;
  overlaySettings: GeometryOverlaySetting[];
  /**
   * Handles a polygon picked for Analyze Area. Together with onSelectOverlayForNote,
   * its presence is what makes overlays selectable at all: without either, the
   * tile prefetch that selection depends on is skipped.
   */
  openAnalyzePolygonPopup?: (f: geojson.Feature<geojson.Polygon | geojson.MultiPolygon>) => void;
  /** Click handlers keyed by the feature collection’s product */
  clickHandlers?: Record<
    string,
    undefined | ((f: I.ImmutableOf<ApiFeature>, fc: HydratedFeatureCollection) => void)
  >;
  readyToSelectOverlayPolygon?: boolean;
  readyToSelectOverlayForNote?: boolean;
  onSelectOverlayForNote?: (geometry: geojson.Geometry) => void;
  onOverlaySelectionError?: (message: string) => void;
  /**
   * Keep overlay hover/pin working even when clickable features are globally
   * disabled. The public/share view disables clickable features to make the
   * property read-only, which would otherwise also suppress overlay tooltips.
   */
  ignoreClickableFeaturesDisabled?: boolean;
}

const USER_LAYERS_POPUP = new mapboxgl.Popup({
  className: cs.popup,
  closeButton: false,
  closeOnClick: false,
  offset: 5,
});

const renderLi = (label: string, value: unknown) =>
  value === undefined || value === null || value === ''
    ? ''
    : `<li>${escapeHtml(label)}: ${escapeHtml(value)}</li>`;

const handleMouseMoveFill = (
  featureCollection: HydratedFeatureCollection,
  event: mapboxgl.MapMouseEvent,
  isPopupFrozen: boolean,
  canPin: boolean
) => {
  if (!event.features?.length) return;

  let htmlContent = '';
  const props = (event.features[0].properties || {}) as Record<string, unknown>;

  // Defne handlers for the specific overlay type
  switch (featureCollection.get('id')) {
    case REGRID_FEATURE_COLLECTION_ID:
      htmlContent = `<ul>
        <li>Parcel ID: ${escapeHtml(event.features[0].properties?.parcelnumb)}</li>
        <li>Owner: ${escapeHtml(event.features[0].properties?.owner || 'Unknown')}</li>
        <li>Address: ${escapeHtml(formatRegridAddress(event.features[0].properties))}</li>
      </ul>`;
      break;

    case INDIGENOUS_TERRITORIES_FEATURE_COLLECTION_ID: {
      const territories: string[] = event.features
        ? event.features.map(
            (f) =>
              `<li><a href="${escapeHtml(
                safeUrl(f.properties?.description)
              )}" target="_blank" rel="noopener noreferrer">${escapeHtml(
                f.properties!.name
              )}</a></li>`
          )
        : [``];
      const uniqueTerritories = Array.from(new Set(territories));
      htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong>
                     <ul>${uniqueTerritories.join('')}</ul>`;
      break;
    }

    case CONSTANTS.HR_FLOWLINES_FEATURE_COLLECTION_ID: {
      const riverNames = Array.from(
        new Set(
          event.features
            .filter((f) => !!f.properties?.gnis_name)
            .map((f) => f.properties!.gnis_name)
        )
      ).join(', ');

      if (!riverNames) return;

      htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong><p>${escapeHtml(
        riverNames
      )}</p>`;
      break;
    }

    case EPA_SUPERFUND_SITES_FEATURE_COLLECTION_ID:
      htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong>
                     <p>${escapeHtml(event.features[0].properties?.SITE_NAME)}</p>
                     <a href="${escapeHtml(
                       safeUrl(event.features[0].properties?.URL_ALIAS_TXT)
                     )}" target='_blank'>Read more here...</a>`;
      break;

    case PLSS_FEATURE_COLLECTION_ID:
    case PLSS_TOWNSHIPS_FEATURE_COLLECTION_ID:
    case PLSS_SECTIONS_FEATURE_COLLECTION_ID: {
      switch (event.features[0].properties?.plss_level) {
        case 'township':
          htmlContent = `<strong>${escapeHtml(props.TWNSHPLAB || 'PLSS Township')}</strong>
          <p>PLSS Township</p>
          <ul>
            ${renderLi('State', props.STATEABBR)}
            ${renderLi('Principal Meridian Code', props.PRINMERCD)}
            ${renderLi('Principal Meridian Name', props.PRINMER)}
            ${renderLi('Township Label', props.TWNSHPLAB)}
            ${renderLi('Township Identifier', props.PLSSID)}
            ${renderLi('Township Number', props.TWNSHPNO)}
            ${renderLi('Township Fraction', props.TWNSHPFRAC)}
            ${renderLi('Township Direction', props.TWNSHPDIR)}
            ${renderLi('Range Number', props.RANGENO)}
            ${renderLi('Range Fraction', props.RANGEFRAC)}
            ${renderLi('Range Direction', props.RANGEDIR)}
            ${renderLi('Township Duplicate Status', props.TWNSHPDPCD)}
            ${renderLi('Steward', props.STEWARD)}
          </ul>`;
          break;
        case 'first_division':
          htmlContent = `<strong>${escapeHtml(
            props.FRSTDIVLAB || props.FRSTDIVTXT || 'PLSS First Division'
          )}</strong>
          <p>PLSS ${escapeHtml(props.FRSTDIVTXT || 'First Division')}</p>
          <ul>
            ${renderLi('First Division Label', props.FRSTDIVLAB)}
            ${renderLi('First Division Type', props.FRSTDIVTXT || props.FRSTDIVTYP)}
            ${renderLi('First Division Identifier', props.FRSTDIVID)}
            ${renderLi('First Division Number', props.FRSTDIVNO)}
            ${renderLi('First Division Duplicate', props.FRSTDIVDUP)}
            ${renderLi('Township Identifier', props.PLSSID)}
            ${renderLi('Survey Type', props.SURVTYPTXT || props.SURVTYP)}
          </ul>`;
          break;

        case 'intersected':
          htmlContent = `<strong>${escapeHtml(
            props.TWNSHPLAB || props.SECDIVLAB || props.FRSTDIVLAB || 'PLSS Intersected'
          )}</strong>
          <p>PLSS Intersected</p>
          <ul>
            ${renderLi('State', props.STATEABBR)}
            ${renderLi('Principal Meridian Code', props.PRINMERCD)}
            ${renderLi('Principal Meridian Name', props.PRINMER)}
            ${renderLi('Township Label', props.TWNSHPLAB)}
            ${renderLi('Township Identifier', props.PLSSID)}
            ${renderLi('Township Number', props.TWNSHPNO)}
            ${renderLi('Township Fraction', props.TWNSHPFRAC)}
            ${renderLi('Township Direction', props.TWNSHPDIR)}
            ${renderLi('Range Number', props.RANGENO)}
            ${renderLi('Range Fraction', props.RANGEFRAC)}
            ${renderLi('Range Direction', props.RANGEDIR)}
            ${renderLi('Township Duplicate Status', props.TWNSHPDPCD)}
            ${renderLi('Steward', props.STEWARD)}

            ${renderLi('First Division Label', props.FRSTDIVLAB)}
            ${renderLi('First Division Identifier', props.FRSTDIVID)}
            ${renderLi('First Division Number', props.FRSTDIVNO)}
            ${renderLi('First Division Duplicate', props.FRSTDIVDUP)}
            ${renderLi('First Division Type', props.FRSTDIVTXT || props.FRSTDIVTYP)}

            ${renderLi('Second Division Label', props.SECDIVLAB)}
            ${renderLi('Second Division Identifier', props.SECDIVID)}
            ${renderLi('Second Division Number', props.SECDIVNO)}
            ${renderLi('Second Division Suffix', props.SECDIVSUF)}
            ${renderLi('Second Division Type', props.SECDIVTXT || props.SECDIVTYP)}
            ${renderLi('Second Division Note', props.SECDIVNOTE)}

            ${renderLi('Quarter Section', props.QSEC)}
            ${renderLi('Quarter-Quarter Section', props.QQSEC)}
            ${renderLi('Government Lot', props.GOVLOT)}

            ${renderLi('Survey Type', props.SURVTYPTXT || props.SURVTYP)}
            ${renderLi('Survey Number', props.SURVNO)}
            ${renderLi('Survey Suffix', props.SURVSUF)}
            ${renderLi('Survey Note', props.SURVNOTE)}
            ${renderLi('Survey Label', props.SURVLAB)}
            ${renderLi('Acres', props.ACRES)}
          </ul>`;
          break;
        default:
          htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong>
          <p>${escapeHtml(event.features[0].properties?.SURVTYPTXT)}</p>`;
          break;
      }
      break;
    }

    default: {
      /*first scenario: the featureCollection is an overlay with multiple hover
      properties stored in overlay metadata view*/
      const fcOverlayMetadataView = (
        featureCollection.get('views') as
          | I.ImmutableListOf<ApiFeatureCollectionOverlayMetadataView>
          | undefined
      )?.find((v) => v?.get('type') === CONSTANTS.VIEW_TYPE_OVERLAY_METADATA);

      const hoverProperties = (
        fcOverlayMetadataView?.get('hover_properties') as I.List<I.Map<string, string>> | undefined
      )?.toJS() as HoverProperty[] | undefined;

      if (hoverProperties?.length && event.features[0].properties) {
        // For overlays whose features can overlap (e.g., year-extent), collect
        // distinct values per hover-property across every feature under the
        // cursor so the popup reflects the whole stack, not just the topmost.
        const processedHoverPropertyHtml = hoverProperties
          .map(({key, label}) => {
            const values = Array.from(
              new Set(
                event
                  .features!.map((f) => f.properties?.[key])
                  .filter((v) => v != null && v !== '')
                  .map((v) => String(v))
              )
            ).sort();
            return values.length
              ? `<li>${escapeHtml(label)}: ${values.map(escapeHtml).join(', ')}</li>`
              : '';
          })
          .filter((s: string) => !!s)
          .join('');
        if (processedHoverPropertyHtml) {
          htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong><ul>${processedHoverPropertyHtml}</ul>`;
          break;
        }
      }
      /*second scenario: the featureCollection has a hoverPropertyKey set directly on the fc*/
      if (
        (featureCollection as HfcWithHoverKey).get('hoverPropertyKey') &&
        event.features[0].properties
      ) {
        const key = (featureCollection as HfcWithHoverKey).get('hoverPropertyKey') as string;
        htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong><p>${escapeHtml(event.features[0].properties[key])}</p>`;
      } else if (event.features[0].properties?.name) {
        /* If we're here, there's no match on a specific handler so we'll just display the name */
        htmlContent = `<strong>${escapeHtml(featureCollection.get('name'))}</strong><p>${escapeHtml(event.features[0].properties.name)}</p>`;
      } else {
        // No content to display
        return;
      }
    }
  }

  if (canPin) {
    htmlContent += `<p class=${cs.popupHelper}>Click to ${isPopupFrozen ? 'unpin' : 'pin'}</p>`;
  }

  return USER_LAYERS_POPUP.setLngLat(event.lngLat).setHTML(htmlContent).addTo(event.target);
};

interface OverlayIdToTileOverlayFCs {
  id: number;
  featureCollection: geojson.FeatureCollection | null;
}

const GeometryOverlaysContent: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  map,
  isMapLoaded,
  selectedFeatures,
  overlayFeatureCollections,
  geoJsonFeaturesLoader,
  isOverlayVisibleByName,
  overlaySettings,
  clickHandlers = {},
  openAnalyzePolygonPopup,
  readyToSelectOverlayPolygon = false,
  readyToSelectOverlayForNote = false,
  onSelectOverlayForNote,
  onOverlaySelectionError,
  ignoreClickableFeaturesDisabled = false,
}) => {
  const isAKeyPressed = hookUtils.useKeyPress('KeyA');
  // Selecting an overlay polygon needs its full tile geometry, which the effect
  // below prefetches and intersects with the property on the main thread. A map
  // with nobody to hand a selection to skips all of it.
  const canSelectOverlay = !!openAnalyzePolygonPopup || !!onSelectOverlayForNote;
  const [tileOverlayFeatureCollections, setTileOverlayFeatureCollections] =
    React.useState<OverlayIdToTileOverlayFCs | null>(null);

  const selectedFeatureGeometry = React.useMemo(() => {
    if (selectedFeatures.size === 1) {
      return selectedFeatures.first().get('geometry').toJS() as
        | geojson.Polygon
        | geojson.MultiPolygon;
    }
    return null;
  }, [selectedFeatures]);

  React.useEffect(() => {
    if (!canSelectOverlay) {
      return;
    }
    const controller = new AbortController();
    // Fetch tiles for overlay features that are enabled.
    // Don't fetch tiles for ones that arent, since they cant be used to select.
    overlayFeatureCollections
      .filter((fc) => !!isOverlayVisibleByName[fc!.get('name')])
      .forEach(fetchTileOverlayFeatureCollections);
    return () => controller.abort();

    async function fetchTileOverlayFeatureCollections(
      ofc: I.ImmutableOf<ApiFeatureCollection> | undefined
    ) {
      // Guard against immutable nonsense about collection elements being nullable or optional.
      if (!ofc || !selectedFeatureGeometry) {
        return;
      }
      const ofcId: number = ofc.get('id');
      // Use the overlay's own maxZoom so tile fetching works for overlays
      // whose tiles only carry features above zoom 10.
      const tileMaxZoom = (ofc.getIn(['tiles', 'maxZoom']) as number | undefined) ?? 10;
      let tileFeatureCollection: geojson.FeatureCollection | null;
      try {
        tileFeatureCollection = await loadTiledFeatureCollectionAsGeoJSON(
          ofc,
          selectedFeatureGeometry,
          {maxZoom: tileMaxZoom, signal: controller.signal}
        );
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        throw e;
      }
      /**
       * The features we get back from this come from the intersection of tiles and feature geometry. In
       * some cases we get back many features that don't intersect the property that don't have unique ids
       * (ex: Building Envelopes). Since we use the id to select the correct tiled features on user click,
       * we want to filter out any gometries outside the property that have non-unique ids before hand. We
       * are doing this premtively instead of upon user click because the intersect is an expensive opperation
       * and can cause a delay between the user click and the shape getting selected on the map. In the case of
       * really large & complicated overlays it can cause the app to hang
       */
      if (tileFeatureCollection) {
        tileFeatureCollection.features = tileFeatureCollection.features.filter((f) => {
          try {
            return (
              (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') &&
              geoJsonUtils.intersectGeometries(
                selectedFeatureGeometry,
                f.geometry as geojson.Polygon | geojson.MultiPolygon
              )
            );
          } catch {
            // Ignore errors and filter out the features that caused them
            return false;
          }
        });
      }
      if (
        tileFeatureCollection &&
        tileFeatureCollection.features.length > 0 &&
        !controller.signal.aborted
      ) {
        setTileOverlayFeatureCollections(
          (prev) =>
            ({
              ...prev,
              [ofcId]: tileFeatureCollection,
            }) as OverlayIdToTileOverlayFCs
        );
      }
    }
    /**
     * Note(anthony): loadTiledFeatureCollectionAsGeoJSON makes requests, so
     * if these values change it'll affect the number of requests to the api.
     */
  }, [
    selectedFeatures,
    isOverlayVisibleByName,
    overlayFeatureCollections,
    selectedFeatureGeometry,
    canSelectOverlay,
  ]);
  const maybeOpenAnalyzePolygonPopup = React.useCallback(
    async (
      overlayFeature: I.ImmutableOf<ApiFeature>,
      overlayFeatureCollection: I.ImmutableOf<ApiFeatureCollection>
    ) => {
      // Analyze Area uses feature data, which is loaded for one feature at once.
      if (selectedFeatures.size !== 1) {
        return;
      }
      const selectedFeature = selectedFeatures.first();

      // Selected in this instance means which overlay feature we're going to use here
      const selectedOverlayFeature = overlayFeature.toJS();
      // Analyze Area only supports Polygon and MultiPolygon geometries.
      const overlayFeatureType = selectedOverlayFeature.geometry.type;
      if (overlayFeatureType !== 'Polygon' && overlayFeatureType !== 'MultiPolygon') {
        return;
      }

      let selectedOverlayFeatureGeometry = selectedOverlayFeature.geometry;

      // If we have overlay features from tiles, these will more accurately cover the active feature
      if (
        tileOverlayFeatureCollections &&
        tileOverlayFeatureCollections[overlayFeatureCollection.get('id')]
      ) {
        // Fetch the features that cover this tile as a fc
        const tileFeatureCollection =
          tileOverlayFeatureCollections[overlayFeatureCollection.get('id')];

        if (tileFeatureCollection) {
          // Our overlay features list from tile coverage should have ids that match the clicked overlay feature
          const filterCriteria = overlayFeature.get('id');
          const fitleredTileFeatures: geojson.Feature[] = tileFeatureCollection.features.filter(
            (f) => filterCriteria == f.id
          );

          if (2 <= fitleredTileFeatures.length) {
            // While fetching and setting our tile features we've already filtered down to Polygons and MultiPolygons
            // But we want to filter down to multipolygon types anyway to ensure we have the right types for compiling them
            selectedOverlayFeatureGeometry = compileGeometry(
              getPolygonGeometryTypesFrom(fitleredTileFeatures)
            );
          } else if (fitleredTileFeatures.length === 1) {
            selectedOverlayFeatureGeometry = fitleredTileFeatures[0].geometry;
          }
        }
      }

      const intersectedFeature = geoJsonUtils.intersectGeometries(
        selectedFeature.get('geometry').toJS(),
        selectedOverlayFeatureGeometry
      );

      // We try to run Analyze Area on the part of the overlay feature that
      // intersects with the selected feature, since that's the only area we can
      // guarantee feature data for.
      // Unfortunately the result of turf/intersect can be an invalid
      // geometry with kinks in it. If this is the case, fall back to the
      // overlay feature. It's possible that this may be outside the bounds
      // of the feature, but Analyze Area will handle that with a more informative
      // error down the line.
      let validOverlayFeature;
      if (intersectedFeature && !kinks(intersectedFeature.geometry).features.length) {
        validOverlayFeature = intersectedFeature;
      } else {
        validOverlayFeature = overlayFeature.toJS();
      }

      const {geometry: intersectedGeometry} = validOverlayFeature;
      openAnalyzePolygonPopup?.(
        geoJsonUtils.feature(
          intersectedGeometry,
          {...overlayFeature.get('properties').toJS()},
          {id: `${intersectedGeometry.type}-from-overlay`}
        )
      );
    },
    [tileOverlayFeatureCollections, selectedFeatures, openAnalyzePolygonPopup]
  );

  const onClick = React.useCallback(
    (f: I.ImmutableOf<ApiFeature>, fc: HydratedFeatureCollection) => {
      if (readyToSelectOverlayForNote && selectedFeatureGeometry) {
        const clickGeometry = f.get('geometry').toJS() as geojson.Geometry;
        const tileFc =
          tileOverlayFeatureCollections && tileOverlayFeatureCollections[fc.get('id')]
            ? tileOverlayFeatureCollections[fc.get('id')]
            : null;
        const geometry = resolveOverlayGeometry(clickGeometry, f.get('id'), tileFc);

        if (
          geometry.type !== 'Point' &&
          geometry.type !== 'Polygon' &&
          geometry.type !== 'MultiPolygon'
        ) {
          onOverlaySelectionError?.(
            "That layer type isn't supported — try a polygon or point overlay."
          );
          return;
        }

        onSelectOverlayForNote?.(geometry);
        return;
      }

      if (canSelectOverlay && (isAKeyPressed || readyToSelectOverlayPolygon)) {
        return maybeOpenAnalyzePolygonPopup(
          f,
          fc as unknown as I.ImmutableOf<ApiFeatureCollection>
        );
      } else {
        const clickHandler = clickHandlers[fc.get('product')];
        return clickHandler?.(f, fc);
      }
    },
    [
      readyToSelectOverlayForNote,
      selectedFeatureGeometry,
      isAKeyPressed,
      readyToSelectOverlayPolygon,
      tileOverlayFeatureCollections,
      onSelectOverlayForNote,
      onOverlaySelectionError,
      maybeOpenAnalyzePolygonPopup,
      clickHandlers,
      canSelectOverlay,
    ]
  );

  const overlayHydratedFeatureCollections: I.List<HydratedFeatureCollection> = React.useMemo(() => {
    return overlayFeatureCollections
      .map((fc) =>
        featureCollectionUtils.hydratedFeatureCollection(fc!, geoJsonFeaturesLoader(fc!))
      )
      .toList();
  }, [overlayFeatureCollections, geoJsonFeaturesLoader]);

  return (
    <>
      {overlayHydratedFeatureCollections
        .map((fc) => {
          const isVisible = !!isOverlayVisibleByName[fc!.get('name')];

          // If the overlay is not visible, don't render the GeometryOverlayContent for it.
          // This improves performance for portfolios with complicated, large, or numerous
          // overlays to not have to do this work until it's needed. It does mean that when
          // you turn on an overlay, it may take a second to appear and is not instant.
          if (!isVisible) {
            return;
          }

          const overlaySetting = overlaySettings.find(({name}) => name === fc!.get('name'));
          const propertyColorRule = overlaySetting?.propertyColorRule ?? null;
          return (
            <GeometryOverlayContent
              key={fc!.get('id')}
              map={map}
              color={overlaySetting?.color || '#000'}
              isMapLoaded={isMapLoaded}
              hydratedFeatureCollection={fc!}
              onClick={onClick}
              isVisible={isVisible}
              defaultFilled={overlaySetting?.defaultFilled || false}
              defaultUnfilled={overlaySetting?.defaultUnfilled || false}
              propertyColorRule={propertyColorRule}
              readyToSelectOverlayForNote={readyToSelectOverlayForNote}
              ignoreClickableFeaturesDisabled={ignoreClickableFeaturesDisabled}
            />
          );
        })
        .toArray()}
    </>
  );
};

type GeometryOverlayContentProps = {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  color: string;
  isVisible: boolean;
  defaultFilled: boolean;
  defaultUnfilled: boolean;
  onClick?:
    | undefined
    | ((
        f: I.ImmutableOf<ApiFeature>,
        fc: HydratedFeatureCollection,
        ev: mapboxgl.MapLayerMouseEvent
      ) => void);

  // percentage decimal value from 0 and 1
  fillOpacityOverride?: number;
  // percentage decimal value from 0 and 1, only applied on mouseenter of the feature
  hoverFillFeatureOpacity?: number;
  hydratedFeatureCollection: HydratedFeatureCollection;
  propertyColorRule?: PropertyColorRule | null;
  readyToSelectOverlayForNote?: boolean;
  ignoreClickableFeaturesDisabled?: boolean;
  /** Turn off when clicking a feature already does something else, like adding a parcel. */
  pinOnClick?: boolean;
};

const GeometryOverlayContent: React.FunctionComponent<
  React.PropsWithChildren<GeometryOverlayContentProps>
> = (props) => {
  const {
    map,
    isMapLoaded,
    color,
    isVisible,
    defaultFilled,
    defaultUnfilled,
    fillOpacityOverride,
    hoverFillFeatureOpacity,
    hydratedFeatureCollection,
    onClick,
    propertyColorRule,
    readyToSelectOverlayForNote,
    ignoreClickableFeaturesDisabled,
    pinOnClick = true,
  } = props;

  // While the user is actively drawing/measuring/dragging, suppress the hover popup so it
  // doesn't obscure the drawing surface. This shared flag is set by DrawLayer, MeasureTool,
  // note point placement, and the compare slider.
  // The share view disables clickable features to make the property read-only;
  // overlays opt out of that so their hover tooltips still work.
  const areClickableFeaturesDisabled =
    useAreClickableFeaturesDisabled() && !ignoreClickableFeaturesDisabled;
  const canPin = pinOnClick && !readyToSelectOverlayForNote;

  const source = useMemo<FeatureCollectionSourceRecord>(
    () => getSourceForFeatureCollection(hydratedFeatureCollection),
    [hydratedFeatureCollection]
  );

  const idPrefix = `UserLayersVector-${hydratedFeatureCollection.get('id')}`;

  const [fillLayer, lineLayer, pointLayer] = useFilledPolygonLayer(
    {
      idPrefix,
      sourceId: source.id,
      sourceLayer: source.sourceLayer,
      color,
      defaultFilled,
      defaultUnfilled,
      fillOpacityOverride,
      hoverFillFeatureOpacity,
      propertyColorRule,
    },
    USER_LAYERS_POPUP
  );

  // Mapbox tracks whether the cursor is inside the layers per subscription, so re-subscribing
  // while hovering can miss the mouseleave that hides the popup. Keep these dependencies stable
  // across renders: layer ids rather than the layer objects, which are rebuilt every render.
  React.useEffect(() => {
    if (!isVisible || areClickableFeaturesDisabled) {
      return;
    }

    const layerIds = [fillLayer.id, lineLayer.id, pointLayer.id];
    let isPopupFrozen = false;
    // Current identity of the hovered feature: the feature ID when promoteId is configured,
    // or JSON-stringified properties as a fingerprint when falling back to property matching.
    let hoveredFeatureFingerprint: string | number | undefined;
    const hoveredFeatureIds = new Set<string | number>();
    // Cache of queryRenderedFeatures for the property-matching fallback path, invalidated on
    // map move/zoom so we don't scan the full viewport on every feature boundary crossing.
    let renderedFeaturesCache: mapboxgl.MapboxGeoJSONFeature[] | null = null;

    const showPopup = (event: mapboxgl.MapMouseEvent) => {
      handleMouseMoveFill(hydratedFeatureCollection, event, isPopupFrozen, canPin);
    };

    const setHoverFeatureState = (id: string | number, hovered: boolean) => {
      map.setFeatureState(
        {source: source.id, ...(source.sourceLayer ? {sourceLayer: source.sourceLayer} : {}), id},
        {hovered}
      );
    };

    const clearHoverStates = () => {
      hoveredFeatureIds.forEach((id) => setHoverFeatureState(id, false));
      hoveredFeatureIds.clear();
      hoveredFeatureFingerprint = undefined;
    };

    const invalidateRenderedFeaturesCache = () => {
      renderedFeaturesCache = null;
    };

    const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
      if (!isPopupFrozen) {
        showPopup(event);
      }
      if (hoverFillFeatureOpacity !== undefined) {
        const hoveredTileFeature = event.features?.[0];
        const featureId = hoveredTileFeature?.id;
        if (hoveredTileFeature !== undefined && featureId !== undefined) {
          if (source.sourceLayer && source.promoteId) {
            // Vector tile source with promoteId: featureId is stable across tiles.
            // setFeatureState propagates to all tile clips automatically.
            if (featureId !== hoveredFeatureFingerprint) {
              clearHoverStates();
              hoveredFeatureFingerprint = featureId;
              setHoverFeatureState(featureId, true);
              hoveredFeatureIds.add(featureId);
            }
          } else if (source.sourceLayer) {
            // Vector tile source without promoteId: features have tile-local IDs, so we scan
            // the viewport for all clips that share this feature's properties.
            // The rendered-features list is cached per map position to avoid rescanning on
            // every feature-boundary crossing during normal cursor movement.
            const fingerprint = JSON.stringify(hoveredTileFeature.properties);
            if (fingerprint !== hoveredFeatureFingerprint) {
              clearHoverStates();
              hoveredFeatureFingerprint = fingerprint;
              const rendered =
                renderedFeaturesCache ?? map.queryRenderedFeatures({layers: [fillLayer.id]});
              renderedFeaturesCache = rendered;
              for (const f of rendered) {
                if (f.id !== undefined && JSON.stringify(f.properties) === fingerprint) {
                  setHoverFeatureState(f.id, true);
                  hoveredFeatureIds.add(f.id);
                }
              }
            }
          } else {
            // GeoJSON source: feature IDs are always stable.
            if (featureId !== hoveredFeatureFingerprint) {
              clearHoverStates();
              hoveredFeatureFingerprint = featureId;
              setHoverFeatureState(featureId, true);
              hoveredFeatureIds.add(featureId);
            }
          }
        }
      }
    };
    const onMouseLeave = () => {
      if (!isPopupFrozen) {
        USER_LAYERS_POPUP.remove();
      }
      clearHoverStates();
    };
    const onClick = (event: mapboxgl.MapMouseEvent) => {
      if (canPin) {
        isPopupFrozen = !isPopupFrozen;
        showPopup(event);
      }
    };

    map.on('moveend', invalidateRenderedFeaturesCache);
    map.on('zoomend', invalidateRenderedFeaturesCache);
    // One subscription across all three layers, so a click on a polygon's edge (where the fill
    // and line overlap) toggles the pin once, and moving between them doesn't count as leaving.
    map.on('mousemove', layerIds, onMouseMove);
    map.on('mouseleave', layerIds, onMouseLeave);
    map.on('click', layerIds, onClick);

    return () => {
      USER_LAYERS_POPUP.remove();
      clearHoverStates();
      map.off('moveend', invalidateRenderedFeaturesCache);
      map.off('zoomend', invalidateRenderedFeaturesCache);
      map.off('mousemove', layerIds, onMouseMove);
      map.off('mouseleave', layerIds, onMouseLeave);
      map.off('click', layerIds, onClick);
    };
  }, [
    map,
    fillLayer.id,
    lineLayer.id,
    pointLayer.id,
    hydratedFeatureCollection,
    isVisible,
    canPin,
    areClickableFeaturesDisabled,
    hoverFillFeatureOpacity,
    source.id,
    source.sourceLayer,
    source.promoteId,
  ]);

  const onClickFeature = React.useMemo(
    () =>
      onClick &&
      hydratedFeatureCollection &&
      ((f: mapboxgl.MapboxGeoJSONFeature, ev: mapboxgl.MapLayerMouseEvent) => {
        let feature: I.ImmutableOf<ApiFeature> | undefined;
        if (source.source.type === 'vector') {
          feature = I.fromJS(geoJsonUtils.feature(f.geometry, f.properties, {id: f.id}));
        } else {
          // We look up the original feature because the one we get from mapbox
          // may be clipped to the viewport or otherwise modified.
          feature = hydratedFeatureCollection
            .get('features')
            .find((fcFeature) => fcFeature!.get('id') === f.id);
        }

        if (feature) {
          onClick(feature, hydratedFeatureCollection, ev);
        }
      }),
    [hydratedFeatureCollection, onClick, source.source.type]
  );

  useClickableFeatures(map, [fillLayer, lineLayer, pointLayer], isVisible && onClickFeature);
  return (
    <MapContent
      map={map}
      isMapLoaded={isMapLoaded}
      sources={[source]}
      layers={isVisible ? [fillLayer, lineLayer, pointLayer] : []}
    />
  );
};

export {GeometryOverlaysContent as default, GeometryOverlayContent};

/**
 * Format the Regrid parcel address by joining the constituent parts. Only
 * include the city, state, and zip code if they are all present to avoid
 * confusion (e.g., if a parcel is in Albany, TX but the TX is missing, one
 * could assume Albany, NY).
 *
 * I considered formatting the address with better casing by generically
 * capitalizing every word in the street and city, and every word in the state
 * if it's spelled out or every letter in the state if it's an abbreviation.
 * This wouldn't have been clever enough, though, to property format city names
 * like "DePaul" with internal capitalizations. It felt like a small can of
 * worms, so leaving everything uppercased for now.
 */
export function formatRegridAddress(properties: geojson.GeoJsonProperties) {
  const {address, scity, state2, szip} = properties || {};
  const parts = [address, ...(address && scity && state2 && szip ? [scity, state2, szip] : [])];
  return parts.join(', ') || 'Unknown';
}

export function resolveOverlayGeometry(
  clickGeometry: geojson.Geometry,
  featureId: geojson.Feature['id'],
  tileFc: geojson.FeatureCollection | null
): geojson.Geometry {
  if (!tileFc) return clickGeometry;

  // Loose equality: tile feature IDs from Mapbox can be strings even when the source data has
  // numeric IDs (e.g. 42 vs "42"), so == is intentional here.
  const tileFeatures = tileFc.features.filter((f) => f.id == featureId);

  if (tileFeatures.length >= 2) {
    return compileGeometry(getPolygonGeometryTypesFrom(tileFeatures)) ?? clickGeometry;
  } else if (tileFeatures.length === 1) {
    return tileFeatures[0].geometry;
  }

  return clickGeometry;
}
